import React, { useState, useEffect } from "react";
import styles from './HourMinuteSelector.module.css';

const HourMinuteSelector = ({ onChange, defaultMinute = 0, maxSelectionInMin = 480, source = 'bookingExtend', duration, minimumBalanceAvailable, minSelectionInMinutes = 15 }) => {
    const safeMinSelectionInMinutes = Math.max(0, Number(minSelectionInMinutes) || 0);
    const clampedMinSelectionInMinutes = Math.min(safeMinSelectionInMinutes, maxSelectionInMin);
    const maxInitialHour = Math.floor(maxSelectionInMin / 60);
    const maxInitialMinute = maxSelectionInMin % 60;
    const allMinutes = [0, 15, 30, 45];

    const getValidMinutesForHour = (selectedHour) => {
        const allowedMinutes = selectedHour === maxInitialHour
            ? Array.from({ length: Math.floor(maxInitialMinute / 15) + 1 }, (_, i) => i * 15)
            : allMinutes;

        return allowedMinutes.filter((minute) => {
            const totalMinutes = selectedHour * 60 + minute;
            return totalMinutes >= clampedMinSelectionInMinutes && totalMinutes <= maxSelectionInMin;
        });
    };

    const normalizeSelection = (selectedHour, selectedMinute) => {
        const validMinutes = getValidMinutesForHour(selectedHour);

        if (!validMinutes.length) {
            const fallbackHour = Math.floor(clampedMinSelectionInMinutes / 60);
            const fallbackMinutes = getValidMinutesForHour(fallbackHour);
            return {
                hour: fallbackHour,
                minute: fallbackMinutes[0] ?? 0,
            };
        }

        const nearestMinute = validMinutes.includes(selectedMinute)
            ? selectedMinute
            : validMinutes[0];

        return {
            hour: selectedHour,
            minute: nearestMinute,
        };
    };

    const resolvedDefaultMinute = Math.max(defaultMinute, clampedMinSelectionInMinutes);
    const initialSelection = normalizeSelection(
        Math.floor(resolvedDefaultMinute / 60),
        resolvedDefaultMinute % 60
    );

    const [hour, setHour] = useState(initialSelection.hour);
    const [minute, setMinute] = useState(initialSelection.minute);
    const [disableSelector, setDisableSelector] = useState(false);

    const hours = Array.from({ length: maxInitialHour + 1 }, (_, i) => i)
        .filter((h) => getValidMinutesForHour(h).length > 0);

    useEffect(() => {
        const updatedSelection = normalizeSelection(
            Math.floor(Math.max(defaultMinute, clampedMinSelectionInMinutes) / 60),
            Math.max(defaultMinute, clampedMinSelectionInMinutes) % 60
        );

        setHour(updatedSelection.hour);
        setMinute(updatedSelection.minute);
    }, [defaultMinute, clampedMinSelectionInMinutes]);

    useEffect(() =>{
        minimumBalanceAvailable ? setDisableSelector(false) :
        (duration === 0 || duration === '') ? setDisableSelector(true) : setDisableSelector(false);
    },[duration, minimumBalanceAvailable])

    const handleChange = (h, m) => {
        const totalMinutes = h * 60 + m;
        if (onChange) {
            onChange(totalMinutes);
        }
    };

    const handleHourChange = (e) => {
        const value = parseInt(e.target.value, 10);
        const validMinutes = getValidMinutesForHour(value);
        const nextMinute = validMinutes[0] ?? 0;

        setHour(value);
        setMinute(nextMinute);
        handleChange(value, nextMinute);
    };

    const handleMinuteChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setMinute(value);
        handleChange(hour, value);
    };

    const filteredMinutes = source === "bookingForm"
        ? getValidMinutesForHour(hour)
        : getValidMinutesForHour(hour).filter((m) => !(hour === 0 && m === 0));

    const selectedMinute = filteredMinutes.includes(minute) ? minute : filteredMinutes[0] || 0;

    useEffect(() => {
        if (filteredMinutes.length > 0 && !filteredMinutes.includes(minute)) {
            const nextMinute = filteredMinutes[0];
            setMinute(nextMinute);
            handleChange(hour, nextMinute);
        }
    }, [hour, minute, filteredMinutes]);

    return (
        <div style={{ display: "flex", gap: "10px", width: "100%" }}>
            <select
                id="hour"
                value={hour}
                onChange={handleHourChange}
                disabled={disableSelector}
                className={`form-select ${source === "bookingForm" ? styles["booking-form-input"] : styles["select-width"]}`}
            >
                {hours.map((h) => (
                    <option key={h} value={h}>
                        {h} hr
                    </option>
                ))}
            </select>

            <select
                id="minute"
                value={selectedMinute}
                onChange={handleMinuteChange}
                disabled={disableSelector || filteredMinutes.length === 0}
                className={`form-select ${source === "bookingForm" ? styles["booking-form-input"] : styles["select-width"]}`}
            >
                {filteredMinutes.map((m) => (
                    <option key={m} value={m}>
                        {m} min
                    </option>
                ))}
            </select>
        </div>
    );
};

export default HourMinuteSelector;
